import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { MatSelectChange } from '@angular/material/select';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { ColumnType } from '@datachain/ui-sdk/components/dc-types';
import { Store } from '@ngrx/store';
import { Map, OrderedMap } from 'immutable';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';

import { ColumnInfo } from '../../../core/dc-core-types';
import { DcIcons } from '../../../ui/app-dc.icons';
import { ValidationErrorKeys } from '../../../ui/form/validation-erros-keys';
import { DataLinkRepository } from '../../domain/depot.types';
import {
	changeVersionForSelectedDataLinkRepo,
	DepotSelector,
} from '../../store';
import { AvailableVersionEntity } from './available-version.entity';

/** What the caller passes in, and what closing the popup resolves with. */
export type ChangeVersionModalData = {
	depot: DataLinkRepository;
};

@Component({
	selector: 'app-depot-change-version-modal',
	templateUrl: './change-version-modal.component.html',
	styleUrls: ['./change-version-modal.component.scss'],
})
export class ChangeVersionModalComponent<T extends ChangeVersionModalData>
	extends DcBaseComponent
	implements OnInit
{
	protected readonly DcIcons = DcIcons;
	protected readonly Appearance: MatFormFieldAppearance = 'outline';
	protected readonly Validators = Validators;
	protected readonly ValidationErrorKeys = ValidationErrorKeys;

	public vo$: Observable<{
		availableVersions: Map<number, AvailableVersionEntity>;
		depot: DataLinkRepository;
		currentVersionColumnsPreview: OrderedMap<string, ColumnInfo>;
		selectedVersion: AvailableVersionEntity | null;
		isLoading: boolean;
	}>;

	public versionControl = new FormControl(null, Validators.required);

	private readonly ColumnTypeMapping: Record<string, ColumnType> = {
		['StringType']: ColumnType.Str,
		['FileType']: ColumnType.File,
		['DecimalType']: ColumnType.Dec,
		['TimestampType']: ColumnType.Date,
		['LongType']: ColumnType.BigInt,
		['big_integer']: ColumnType.BigInt,
		['BinaryType']: ColumnType.Bin,
		['WordsType']: ColumnType.Words,
		['IntegerType']: ColumnType.Int,
		['BooleanType']: ColumnType.Bool,
		['GeometryType']: ColumnType.Geo,
	};

	private readonly selectedVersion$ =
		new BehaviorSubject<AvailableVersionEntity | null>(null);

	public constructor(
		private readonly modalService: AppModalService,
		private readonly selector: DepotSelector,
		private readonly store: Store,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'depot-change-version-modal';
		this.versionControl.markAsTouched();
		this.versionControl.updateValueAndValidity();

		const currentVersionColumnsPreview: OrderedMap<string, ColumnInfo> =
			OrderedMap(
				this.modalData.depot.headers.map((header) => [
					header.id,
					{
						uuid: header.id,
						label: header.label,
						type: this.ColumnTypeMapping[header.type],
						isList: header.isList,
					},
				])
			);

		this.vo$ = combineAll({
			availableVersions:
				this.selector.getAvailableVersionsForSelectDataLinkRepo$(),
			depot: this.modalData.depot,
			currentVersionColumnsPreview,
			selectedVersion: this.selectedVersion$,
			isLoading: this.selector.getLoading$(),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
	}

	public async onVersionChange(e: MatSelectChange): Promise<void> {
		const selectedId = e.value;

		const allVersions = await firstValueFrom(
			this.selector.getAvailableVersionsForSelectDataLinkRepo$()
		);

		const selectedVersion = allVersions.get(selectedId);

		if (!selectedVersion) {
			return;
		}

		this.selectedVersion$.next(selectedVersion);
	}

	/** Closing with undefined is how the caller tells a cancel from a save. */
	public onCancel(): void {
		this.modalService.closeActiveModal(undefined);
	}

	public async onSave(): Promise<void> {
		const selectedVersion = await firstValueFrom(this.selectedVersion$);

		if (!selectedVersion) {
			return;
		}

		this.store.dispatch(
			changeVersionForSelectedDataLinkRepo({
				depotId: this.modalData.depot.id,
				newVersion: selectedVersion.id,
			})
		);
	}

	public isValid(): boolean {
		return true;
	}
}
